import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Building2, ChevronDown, ChevronLeft, ChevronRight, FileSignature, FileText, IdCard, Lock, Mail, Monitor, PenLine, ScanFace, UserCheck, UserRound } from 'lucide-react';
import { SelectionActionBar, SelectionCheckbox, useTableSelection } from '../components/TableSelection';
import { lastUpdatedTimestamp } from '../lib/timestamps';
import { workspaceAccessMessage } from '../lib/workspaceAccess';

const fmtPeriod = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

function startOfUtcMonth(date = new Date()) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addUtcMonths(date, amount) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1));
}

function monthKey(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function monthLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const USAGE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'verification', label: 'Verifications' },
  { value: 'signature', label: 'Documents' },
];

function ResourceCard({ title, used, pending, available, included, barTestId, locked = false }) {
  if (locked) {
    return (
      <section className="border border-zinc-200 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-semibold">{title}</h2>
            <p className="text-sm text-zinc-500 mt-1">Free tier locked</p>
          </div>
          <span className="inline-flex h-6 min-w-6 items-center justify-center border border-zinc-200 bg-white px-1.5 text-zinc-500" title="Workspace verification required">
            <Lock className="w-3 h-3" strokeWidth={1.8}/>
          </span>
        </div>
      </section>
    );
  }
  const safeIncluded = included || 1;
  const usedPct = Math.min(100, Math.max(0, (used / safeIncluded) * 100));
  const pendingPct = Math.min(100 - usedPct, Math.max(0, (pending / safeIncluded) * 100));
  return (
    <section className="border border-zinc-200 p-6">
      <div>
        <h2 className="font-display text-xl font-semibold">{title}</h2>
        <p className="text-sm text-zinc-500 mt-1">
          <span className="font-mono text-zinc-900">{used} / {included}</span> included
        </p>
      </div>
      <div className="mt-4 h-2 bg-zinc-100 rounded-sm overflow-hidden flex">
        <div
          data-testid={barTestId}
          className="h-full bg-zinc-900"
          style={{ width: `${usedPct}%` }}
        />
        <div
          data-testid={`${barTestId}-pending`}
          className="h-full bg-zinc-400"
          style={{ width: `${pendingPct}%` }}
        />
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Used</div>
          <div className="font-display text-xl font-bold mt-1">{used}</div>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Reserved</div>
          <div className="font-display text-xl font-bold mt-1">{pending}</div>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Available</div>
          <div className="font-display text-xl font-bold mt-1">{available}</div>
        </div>
      </div>
    </section>
  );
}

function MethodBadge({ item }) {
  const type = String(item.activity_type || '').toLowerCase();
  const isSignature = type === 'signature';
  const isBusiness = type === 'business' || type === 'business_verification';
  const method = isSignature
    ? (item.auth_method === 'kyc' || item.signature_level === 'AES' ? 'AES' : 'SES')
    : isBusiness
      ? 'KYB'
      : (isIdvVerification(item) ? 'IDV' : 'KYC');
  const ageLabel = ageRequirementLabel(item.min_age);
  const methodIcon = (() => {
    if (method === 'KYC') {
      return <UserCheck className="w-3 h-3" strokeWidth={1.8}/>;
    }
    if (method === 'IDV') {
      return <IdCard className="w-3 h-3" strokeWidth={1.8}/>;
    }
    if (method === 'AES' || method === 'SES') {
      return <FileSignature className="w-3 h-3" strokeWidth={1.8}/>;
    }
    if (method === 'KYB') {
      return <Building2 className="w-3 h-3" strokeWidth={1.8}/>;
    }
    return null;
  })();
  return (
    <span className="inline-flex flex-wrap items-center">
      <BadgeSequence>
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-white px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
        {methodIcon}
        {method}
      </span>
      {(method === 'KYC' || method === 'IDV') && ageLabel && <AgeRequirementBadge value={item.min_age}/>}
      </BadgeSequence>
    </span>
  );
}

function workflowMethodForActivity(item) {
  const type = String(item.activity_type || '').toLowerCase();
  if (type === 'signature') {
    return item.auth_method === 'kyc' || item.signature_level === 'AES' ? 'AES' : 'SES';
  }
  if (type === 'verification') {
    return isIdvVerification(item) ? 'IDV' : 'KYC';
  }
  return '';
}

function isIdvVerification(item) {
  const method = String(item?.verification_method || item?.method || '').toLowerCase();
  return method === 'idv' || method === 'id_only' || item?.requires_selfie === false;
}

function WorkflowStepBadge({ icon: Icon, title, variant = 'default' }) {
  const variantClass = variant === 'external'
    ? 'rounded-lg border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
    : variant === 'linked'
      ? 'rounded-full border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
      : 'rounded-full border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500';
  return (
    <span className={`inline-flex h-6 min-w-6 items-center justify-center border px-1.5 ${variantClass}`} title={title} aria-label={title}>
      <Icon className="h-3 w-3" strokeWidth={1.8}/>
    </span>
  );
}

function BadgeSequence({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <span className="inline-flex items-center">
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span aria-hidden="true" className="h-px w-2 bg-zinc-200"/>}
          {child}
        </React.Fragment>
      ))}
    </span>
  );
}

function ageRequirementLabel(value) {
  const age = Number(value);
  return [16, 18, 21].includes(age) ? `${age}+` : '';
}

function AgeRequirementBadge({ value }) {
  const label = ageRequirementLabel(value);
  if (!label) return null;
  return (
    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-zinc-200 bg-white px-1.5 text-[10px] font-mono tracking-widest text-zinc-500" title={`Age requirement: ${label}`} aria-label={`Age requirement: ${label}`}>
      {label}
    </span>
  );
}

function WorkflowBadges({ item }) {
  const method = workflowMethodForActivity(item);
  const linkedToDocument = !!(
    item.linked_signature_id ||
    item.signature_id ||
    item.linked_document_id ||
    item.related_signature ||
    item.related_document ||
    item.related_type === 'signature'
  );
  if (method === 'AES') {
    return (
      <BadgeSequence>
        <WorkflowStepBadge icon={UserCheck} title="KYC" variant="linked"/>
        <WorkflowStepBadge icon={PenLine} title="Signature"/>
      </BadgeSequence>
    );
  }
  if (method === 'SES') {
    return (
      <BadgeSequence>
        <WorkflowStepBadge icon={Mail} title="Email signing"/>
        <WorkflowStepBadge icon={PenLine} title="Signature"/>
      </BadgeSequence>
    );
  }
  if (method === 'KYC') {
    return (
      <BadgeSequence>
        {item.device_ip_analysis_enabled && <WorkflowStepBadge icon={Monitor} title="Device and IP analysis"/>}
        <WorkflowStepBadge icon={IdCard} title="ID check"/>
        <WorkflowStepBadge icon={ScanFace} title="Selfie check"/>
        {linkedToDocument && <WorkflowStepBadge icon={FileSignature} title="Document signing" variant="external"/>}
      </BadgeSequence>
    );
  }
  if (method === 'IDV') {
    return (
      <BadgeSequence>
        {item.device_ip_analysis_enabled && <WorkflowStepBadge icon={Monitor} title="Device and IP analysis"/>}
        <WorkflowStepBadge icon={IdCard} title="ID check"/>
        {linkedToDocument && <WorkflowStepBadge icon={FileSignature} title="Document signing" variant="external"/>}
      </BadgeSequence>
    );
  }
  return null;
}

function StatusBadge({ status, displayStatus }) {
  const normalized = String(status || '').toLowerCase();
  const className = ['approved', 'signed', 'completed'].includes(normalized)
    ? 'text-emerald-700 border-emerald-300 bg-emerald-50'
      : ['needs_review', 'needs_action', 'needs_action_required', 'action_required', 'manual_review'].includes(normalized)
      ? 'text-amber-800 border-amber-300 bg-amber-50'
      : normalized === 'rejected'
      ? 'text-red-700 border-red-300 bg-red-50'
      : ['revoked', 'expired', 'failed'].includes(normalized)
        ? 'text-red-700 border-red-300 bg-red-50'
        : 'text-zinc-600 border-zinc-200 bg-zinc-50';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${className}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {displayStatus || String(status || '').replaceAll('_', ' ')}
    </span>
  );
}

function activityRoute(item) {
  const type = String(item.activity_type || '').toLowerCase();
  if (type === 'signature') return `/dashboard/signatures/${item.id}`;
  if (type === 'business' || type === 'business_verification') return `/dashboard/businesses/${item.id}`;
  return `/dashboard/verifications/${item.id}`;
}

function usageSelectionId(item) {
  return `${item.activity_type}-${item.id}`;
}

function activityName(item) {
  const type = String(item.activity_type || '').toLowerCase();
  if (type === 'signature') {
    return {
      Icon: FileText,
      label: item.title || 'Untitled document',
    };
  }
  if (type === 'business' || type === 'business_verification') {
    return {
      Icon: Building2,
      label: item.business_name || item.company_name || item.name || 'Unnamed business',
    };
  }
  const claimedName = [item.claimed_first_name, item.claimed_last_name].filter(Boolean).join(' ');
  return {
    Icon: UserRound,
    label: claimedName || item.extracted_name || item.applicant_email || 'Unnamed applicant',
  };
}

function activityReference(item) {
  const type = String(item.activity_type || '').toLowerCase();
  if (type === 'signature') {
    const signerName = item.extracted_name || item.signer_name;
    if (signerName) return { value: signerName, label: 'Signer', monospace: false };
    const signerEmail = item.applicant_email || item.signer_email;
    if (signerEmail) return { value: signerEmail, label: 'Mail', monospace: true };
    const signatureReference = item.signature_reference || item.public_reference || item.public_token_prefix;
    if (signatureReference) return { value: signatureReference, label: 'Signature reference', monospace: true };
    return { value: '—', label: '', monospace: true };
  }
  if (type === 'business' || type === 'business_verification') {
    if (item.enterprise_number) return { value: item.enterprise_number, label: 'Enterprise number', monospace: true };
    const businessReference = item.business_reference || item.reference;
    if (businessReference) return { value: businessReference, label: 'Business reference', monospace: true };
    return { value: '—', label: '', monospace: true };
  }
  if (item.extracted_document_number) return { value: item.extracted_document_number, label: 'ID card', monospace: true };
  return { value: 'Not received', label: '', monospace: true, missing: true };
}

export default function Usage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => startOfUtcMonth());
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityFilterOpen, setActivityFilterOpen] = useState(false);
  const selectedMonthKey = monthKey(selectedMonth);

  useEffect(() => {
    setError(null);
    api.get('/usage', { params: { month: selectedMonthKey } })
      .then(({ data }) => setData(data))
      .catch((e) => setError(workspaceAccessMessage(e, 'Usage data could not be loaded.')));
  }, [selectedMonthKey]);

  const recentRows = data?.recent || [];
  const filteredRecent = recentRows.filter((item) => activityFilter === 'all' || item.activity_type === activityFilter);
  const tableSelection = useTableSelection(filteredRecent, usageSelectionId);

  if (error) {
    return <div className="border border-red-200 bg-red-50 p-6 text-red-800" data-testid="usage-error">
      <p className="text-xs font-mono uppercase tracking-widest text-red-500">Usage</p>
      <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-red-950">Usage unavailable</h1>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-red-800">{error}</p>
    </div>;
  }
  if (!data) return <p className="text-sm text-zinc-500" data-testid="usage-loading">Loading…</p>;

  const { usage, pricing, period } = data;
  const freeTierLocked = Boolean(data.workspace_verification?.free_tier_locked);
  const currentMonth = startOfUtcMonth();
  const nextDisabled = selectedMonth >= currentMonth;
  const freeVerifications = pricing.free_verifications_included || pricing.free_included;
  const freeSignatures = pricing.free_signatures_included || 10;
  const selectedActivityFilterLabel = USAGE_FILTERS.find((item) => item.value === activityFilter)?.label || 'All';
  const activityFilterLabel = activityFilter === 'all' ? 'Type' : `Type: ${selectedActivityFilterLabel}`;

  return (
    <div data-testid="usage-page" className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Insights</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Usage</h1>
          <p className="text-sm text-zinc-500 mt-2">
            Period <span className="font-mono">{fmtPeriod(period.start)} → {fmtPeriod(period.end)}</span>
          </p>
        </div>
        <div className="inline-flex w-fit items-center border border-zinc-200 rounded-sm bg-white" aria-label="Usage period navigation">
          <button
            type="button"
            onClick={() => setSelectedMonth((value) => addUtcMonths(value, -1))}
            className="inline-flex h-9 items-center gap-1.5 border-r border-zinc-200 px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:bg-zinc-50"
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5}/>
            Previous month
          </button>
          <div className="min-w-[96px] px-4 text-center font-mono text-xs text-zinc-700">
            {monthLabel(selectedMonth)}
          </div>
          <button
            type="button"
            onClick={() => setSelectedMonth((value) => addUtcMonths(value, 1))}
            disabled={nextDisabled}
            className={`inline-flex h-9 items-center gap-1.5 border-l border-zinc-200 px-3 text-[10px] font-medium uppercase tracking-widest ${nextDisabled ? 'cursor-not-allowed text-zinc-300' : 'text-zinc-500 hover:bg-zinc-50'}`}
          >
            Next month
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5}/>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ResourceCard
          title="Verifications"
          used={usage.total_completed}
          included={freeVerifications}
          pending={usage.verifications_pending || 0}
          available={usage.verifications_available ?? usage.free_remaining}
          barTestId="free-tier-bar"
          locked={freeTierLocked}
        />
        <ResourceCard
          title="Documents"
          used={usage.signatures_used}
          included={freeSignatures}
          pending={usage.signatures_pending || 0}
          available={usage.signatures_available ?? usage.signatures_free_remaining}
          barTestId="signature-tier-bar"
          locked={freeTierLocked}
        />
      </div>

      {freeTierLocked && (
        <section className="border border-zinc-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-zinc-600">
              Verify your identity to activate your included verifications and documents.
            </p>
            <button type="button" onClick={() => navigate('/settings')}
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800">
              Verify identity
            </button>
          </div>
        </section>
      )}

      {usage.sandbox_completed > 0 && (
        <p className="text-xs text-zinc-500 font-mono">
          {usage.sandbox_completed} sandbox KYC verification{usage.sandbox_completed === 1 ? '' : 's'} this period <span className="text-zinc-400">(not counted)</span>.
        </p>
      )}

      <div>
        <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
          <div
            className="relative"
            data-testid="usage-activity-filter"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setActivityFilterOpen(false);
              }
            }}
          >
            <button
              type="button"
              onClick={() => setActivityFilterOpen((open) => !open)}
              className="h-[34px] inline-flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:border-zinc-300"
              aria-haspopup="listbox"
              aria-expanded={activityFilterOpen}
            >
              {activityFilterLabel}
              <ChevronDown className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5}/>
            </button>
            {activityFilterOpen && (
              <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-44 border border-zinc-200 rounded-sm bg-white py-1">
                {USAGE_FILTERS.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      setActivityFilter(item.value);
                      setActivityFilterOpen(false);
                    }}
                    data-testid={`usage-filter-${item.value}`}
                    className={`block w-full text-left px-3 py-2 text-[10px] font-medium uppercase tracking-widest ${activityFilter === item.value ? 'text-zinc-500' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                    role="option"
                    aria-selected={activityFilter === item.value}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <SelectionActionBar count={tableSelection.selectedCount} onClear={tableSelection.clearSelection}/>
        <section className="border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="recent-activity">
          <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-xs uppercase tracking-widest text-zinc-500 text-left">
              <th className="w-10 px-4 py-3 align-middle font-medium">
                <SelectionCheckbox
                  checked={tableSelection.allVisibleSelected}
                  indeterminate={tableSelection.someVisibleSelected && !tableSelection.allVisibleSelected}
                  onCheckedChange={tableSelection.toggleAllVisible}
                  ariaLabel="Select all visible usage activity rows"
                />
              </th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Identifier</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">Workflow</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecent.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-zinc-500" data-testid="recent-empty">No activity yet this period.</td></tr>
            ) : filteredRecent.map((r) => {
              const rowKey = usageSelectionId(r);
              const route = activityRoute(r);
              const { Icon, label } = activityName(r);
              const reference = activityReference(r);
              const lastUpdatedAt = lastUpdatedTimestamp(r);
              const isSelected = tableSelection.isSelected(rowKey);
              return (
                <tr key={rowKey} onClick={() => navigate(route)}
                  className={`border-b border-zinc-100 last:border-0 cursor-pointer ${isSelected ? 'bg-zinc-50/80' : 'hover:bg-zinc-50/60'}`}
                  data-testid={`recent-row-${rowKey}`}>
                  <td className="w-10 px-4 py-4 align-middle">
                    <SelectionCheckbox
                      checked={isSelected}
                      onCheckedChange={() => tableSelection.toggleRow(rowKey)}
                      ariaLabel={`Select ${label}`}
                    />
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <div className="flex items-start gap-2 min-w-0">
                      <Icon className="w-4 h-4 mt-0.5 text-zinc-400 shrink-0" strokeWidth={1.5}/>
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[12rem]" title={label}>{label}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <div className="min-w-0">
                      {reference.missing ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
                          {reference.value}
                        </span>
                      ) : (
                        <div className={`${reference.monospace ? 'font-mono text-xs text-zinc-500' : 'text-sm leading-tight text-zinc-700'} truncate`}>
                          {reference.value}
                        </div>
                      )}
                      {reference.label && <div className={`${reference.monospace ? 'mt-1 leading-4' : 'mt-0.5 leading-tight'} text-xs text-zinc-500`}>{reference.label}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <MethodBadge item={r}/>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <WorkflowBadges item={r}/>
                  </td>
                  <td className="px-4 py-4 align-middle">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge status={r.status} displayStatus={r.display_status}/>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-500">
                    {lastUpdatedAt ? new Date(lastUpdatedAt).toLocaleString() : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </section>
      </div>
    </div>
  );
}
